import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePreferences } from '../contexts/PreferencesContext';
import api, { setVisibleCameras, getQueueStatus } from '../services/api';
import AdvancedCameraSearch from '../components/AdvancedCameraSearch';
import CameraPreferences from '../components/CameraPreferences';
import BreadcrumbNavigation from '../components/BreadcrumbNavigation';
import PictureInPicture from '../components/PictureInPicture';
import ScreenManager from '../components/ScreenManager';
import DragDropScreenBuilder from '../components/DragDropScreenBuilder';
import ScreenThumbnailSelector from '../components/ScreenThumbnailSelector';
import FloatingControls from '../components/FloatingControls';
import AutoRotationManager from '../components/AutoRotationManager';
import SimpleCameraStream from '../components/SimpleCameraStream';
import CachedSnapshot from '../components/CachedSnapshot';
import GoogleMapsView from '../components/GoogleMapsView';
import {
    Maximize2,
    Minimize2,
    Camera,
    Heart,
    Search,
    Grid,
    Monitor,
    Settings,
    Play,
    Plus,
    Eye,
    Layout,
    Users,
    Clock,
    Video,
    ChevronLeft,
    ChevronRight,
    X,
    RefreshCw,
    Star,
    Map,
    Volume2,
    VolumeX
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';

const CameraMonitoring = () => {
    const { hasPermission } = useAuth();
    const location = useLocation();
    const { 
        preferences, 
        updateNestedPreference, 
        addRecentCamera, 
        toggleFavorite,
        getActiveScreen,
        setActiveScreen,
        createScreen,
        updateScreen,
        updateAutoRotation,
        getScreens,
        startAutoRotation
    } = usePreferences();
    const [availableCameras, setAvailableCameras] = useState([]);
    const [filteredCameras, setFilteredCameras] = useState([]);
    const [selectedCameras, setSelectedCameras] = useState([]);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [pipCamera, setPipCamera] = useState(null);
    const [showPip, setShowPip] = useState(false);
    const [showSimpleView, setShowSimpleView] = useState(false);

    // New dashboard UI state
    const [showDashboardView, setShowDashboardView] = useState(true);
    const [currentView, setCurrentView] = useState('live'); // 'live', 'favourites', 'custom', 'map'
    const [onlineStatus, setOnlineStatus] = useState('all'); // 'all', 'online', 'offline'
    const [selectedLocations, setSelectedLocations] = useState(['ALL']);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [dashboardGridLayout, setDashboardGridLayout] = useState('3x3');
    const [fullscreenGridLayout, setFullscreenGridLayout] = useState('3x3');
    const [currentPage, setCurrentPage] = useState(0);
    const [fullscreenPage, setFullscreenPage] = useState(0);
    
    // Dynamic filter options
    const [locationOptions, setLocationOptions] = useState(['ALL']);
    const [categoryOptions, setCategoryOptions] = useState([]);
    
    // Fullscreen camera modal
    const [fullscreenCamera, setFullscreenCamera] = useState(null);
    const [showFullscreenModal, setShowFullscreenModal] = useState(false);
    const [previousViewMode, setPreviousViewMode] = useState(null);

    // Simplified view modes for non-technical users
    const [viewMode, setViewMode] = useState('live'); // 'live', 'screen', 'favorites', 'map'
    const [gridSize, setGridSize] = useState(4); // 1, 4, 9, 16 - like Hikvision
    const [showAllCameras, setShowAllCameras] = useState(true);

    // Screen management state
    const [showScreenManager, setShowScreenManager] = useState(false);
    const [showScreenBuilder, setShowScreenBuilder] = useState(false);
    const [editingScreenId, setEditingScreenId] = useState(null);
    const [screenCameras, setScreenCameras] = useState([]);
    const [rotationState, setRotationState] = useState(null);
    const [thumbnailKeys, setThumbnailKeys] = useState({});
    const [queueInfo, setQueueInfo] = useState(null);
    const [audioStates, setAudioStates] = useState({}); // Track audio on/off state per camera

    const fullscreenRef = useRef(null);
    const searchRef = useRef(null);

    // Refresh thumbnail function
    const refreshThumbnail = (cameraId) => {
        setThumbnailKeys(prev => ({
            ...prev,
            [cameraId]: (prev[cameraId] || 0) + 1
        }));
    };

    // Toggle audio function
    const toggleAudio = (cameraId) => {
        setAudioStates(prev => {
            const currentState = prev[cameraId] || false;
            const newState = !currentState;
            const camera = availableCameras.find(c => c._id === cameraId);
            if (camera) {
                toast.success(`Audio ${newState ? 'enabled' : 'disabled'} for ${camera.name}`, {
                    duration: 2000
                });
            }
            return {
                ...prev,
                [cameraId]: newState
            };
        });
    };

    // Auto-loading streams with GIF placeholders
    const [loadingStreamCameras, setLoadingStreamCameras] = useState(new Set());
    const [readyStreamCameras, setReadyStreamCameras] = useState(new Set());
    const [autoLoadCameras, setAutoLoadCameras] = useState(new Set());
    const [gifThumbnailMode, setGifThumbnailMode] = useState(new Set()); // Cameras showing GIF thumbnails
    const [streamTransitioning, setStreamTransitioning] = useState(new Set()); // Cameras transitioning to stream
    
    // Auto-start with GIF thumbnails, then transition to live streams
    useEffect(() => {
        if (availableCameras && availableCameras.length > 0) {
            // Get the currently visible cameras only
            const visibleCameras = getDashboardFilteredCameras();
            
            // Initially show GIF thumbnails for visible cameras
            const gifTimer = setTimeout(() => {
                visibleCameras.forEach(camera => {
                    if (camera.isOnline) {
                        setGifThumbnailMode(prev => new Set([...prev, camera._id]));
                    }
                });
            }, 200); // Quick GIF loading
            
            // Then start loading live streams for visible cameras only
            const streamTimer = setTimeout(() => {
                visibleCameras.forEach(camera => {
                    if (camera.isOnline && !autoLoadCameras.has(camera._id)) {
                        console.log('🔄 Auto-starting live stream for camera:', camera.name);
                        setAutoLoadCameras(prev => new Set([...prev, camera._id]));
                        setStreamTransitioning(prev => new Set([...prev, camera._id]));
                        setLoadingStreamCameras(prev => new Set([...prev, camera._id]));
                    }
                });
            }, 1500); // Wait 1.5 seconds for users to see GIFs
            
            return () => {
                clearTimeout(gifTimer);
                clearTimeout(streamTimer);
            };
        }
    }, [availableCameras, currentPage, searchTerm, selectedLocations, selectedCategories, dashboardGridLayout]);
    
    // Handle stream ready callback - transition from GIF to live stream
    const handleStreamReady = (cameraId) => {
        console.log('🔴 Live stream ready for camera:', cameraId, '- hiding GIF overlay...');
        
        // Remove from loading and transitioning states
        setLoadingStreamCameras(prev => {
            const newSet = new Set(prev);
            newSet.delete(cameraId);
            return newSet;
        });
        
        setStreamTransitioning(prev => {
            const newSet = new Set(prev);
            newSet.delete(cameraId);
            return newSet;
        });
        
        setGifThumbnailMode(prev => {
            const newSet = new Set(prev);
            newSet.delete(cameraId);
            return newSet;
        });
        
        // Mark stream as ready (this will hide the GIF overlay)
        setReadyStreamCameras(prev => new Set([...prev, cameraId]));
        
        console.log(`✅ LIVE: Stream is now active, GIF overlay hidden`);
    };
    
    // Handle stream error callback - fall back to GIF mode
    const handleStreamError = (cameraId, error) => {
        console.log('Stream error for camera:', cameraId, error, '- falling back to GIF mode');
        
        // Remove from loading and transitioning states
        setLoadingStreamCameras(prev => {
            const newSet = new Set(prev);
            newSet.delete(cameraId);
            return newSet;
        });
        
        setStreamTransitioning(prev => {
            const newSet = new Set(prev);
            newSet.delete(cameraId);
            return newSet;
        });
        
        // Fall back to GIF thumbnail mode
        setGifThumbnailMode(prev => new Set([...prev, cameraId]));
        
        console.log(`📷 GIF: Camera fell back to GIF thumbnail mode due to stream error`);
    };
    
    const toggleCameraMode = (cameraId) => {
        // Manual toggle to go back to GIF mode or force stream mode
        setReadyStreamCameras(prev => {
            const newSet = new Set(prev);
            if (newSet.has(cameraId)) {
                newSet.delete(cameraId);
            } else {
                newSet.add(cameraId);
                setLoadingStreamCameras(prev => new Set([...prev, cameraId]));
            }
            return newSet;
        });
    };

    // Compute visible cameraIds from current grid/page
    const computeVisibleCameraIds = useCallback(() => {
        try {
            const inView = (selectedCameras && selectedCameras.length > 0 ? selectedCameras : filteredCameras) || [];
            const limit = Math.max(1, Number(gridSize) || 4);
            return inView.slice(0, limit).map(c => c._id);
        } catch (e) {
            return [];
        }
    }, [selectedCameras, filteredCameras, gridSize]);

    // Post visible cameras when view changes
    useEffect(() => {
        const ids = computeVisibleCameraIds();
        setVisibleCameras(ids);
        getQueueStatus().then(setQueueInfo).catch(() => {});
    }, [computeVisibleCameraIds]);

    useEffect(() => {
        fetchCameras();

        // Listen for fullscreen change events
        const handleFullscreenChange = () => {
            const isNowFullscreen = !!document.fullscreenElement;
            setIsFullscreen(isNowFullscreen);
            
            // Simplified fullscreen behavior for non-tech users
            if (isNowFullscreen) {
                // Reset fullscreen page to 0
                setFullscreenPage(0);
                
                // The actual camera loading is handled by the useEffect that watches fullscreen state
                toast.success('Fullscreen Mode - Press ESC to exit', {
                    duration: 3000,
                    style: {
                        background: '#000',
                        color: '#fff',
                        border: '1px solid #333'
                    }
                });
            }
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('mozfullscreenchange', handleFullscreenChange);
        document.addEventListener('MSFullscreenChange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
            document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
        };
    }, []);

    // Initialize with simple live view
    useEffect(() => {
        const screens = getScreens();
        if (screens.length === 0) {
            // Create a default "All Cameras" view for non-tech users
            setViewMode('live');
        } else {
            setViewMode('screen');
        }
    }, [preferences.screens]);

    // Load cameras for active screen
    const loadScreenCameras = (screen) => {
        if (!screen || !screen.cameras) {
            setScreenCameras([]);
            return;
        }

        const cameras = screen.cameras.map(screenCamera => {
            const cameraData = availableCameras.find(c => c._id === screenCamera.cameraId);
            return cameraData ? { ...cameraData, ...screenCamera } : null;
        }).filter(Boolean);

        setScreenCameras(cameras);
    };

    // Update screen cameras when active screen or available cameras change
    useEffect(() => {
        const activeScreen = getActiveScreen();
        if (activeScreen) {
            loadScreenCameras(activeScreen);
        }
    }, [preferences.screens.activeScreenId, availableCameras]);

    // Initialize cameras when fullscreen page or layout changes
    useEffect(() => {
        if (!isFullscreen) return;
        
        console.log('Fullscreen camera loading triggered:', {
            isFullscreen,
            fullscreenPage,
            fullscreenGridLayout,
            currentView
        });
        
        // Clear all previous cameras
        setAutoLoadCameras(new Set());
        setReadyStreamCameras(new Set());
        setGifThumbnailMode(new Set());
        
        // Small delay to ensure state is cleared
        const loadTimer = setTimeout(() => {
            // Calculate visible cameras based on current view
            let visibleCameras = [];
            
            if (currentView === 'custom') {
                // For custom mode, get actual camera objects from screenCameras positions
                visibleCameras = [];
                if (screenCameras && screenCameras.length > 0) {
                    screenCameras.forEach(sc => {
                        const camera = availableCameras.find(c => c._id === (sc._id || sc.cameraId));
                        if (camera) {
                            visibleCameras.push(camera);
                        }
                    });
                }
                console.log('Custom mode cameras:', visibleCameras.map(c => c.name));
            } else {
                // For live/favourites mode, calculate based on fullscreen page and grid
                const filteredCameras = getDashboardFilteredCameras();
                const fullscreenGrid = getFullscreenGridDimensions();
                const fullscreenCapacity = fullscreenGrid.rows * fullscreenGrid.cols;
                const startIndex = fullscreenPage * fullscreenCapacity;
                const endIndex = Math.min(startIndex + fullscreenCapacity, filteredCameras.length);
                visibleCameras = filteredCameras.slice(startIndex, endIndex);
                
                console.log('Calculated visible cameras:', {
                    totalFiltered: filteredCameras.length,
                    gridSize: `${fullscreenGrid.rows}x${fullscreenGrid.cols}`,
                    capacity: fullscreenCapacity,
                    startIndex,
                    endIndex,
                    visibleCount: visibleCameras.length
                });
            }
            
            console.log('Loading cameras for fullscreen:', visibleCameras.map(c => c?.name));
            
            // Initialize only visible cameras
            if (visibleCameras.length > 0) {
                // Start with GIF thumbnails immediately
                const gifCameras = new Set();
                visibleCameras.forEach(camera => {
                    if (camera && camera.isOnline) {
                        gifCameras.add(camera._id);
                    }
                });
                setGifThumbnailMode(gifCameras);
                console.log('Set GIF mode for cameras:', Array.from(gifCameras));
                
                // Then load streams after a delay
                const streamTimer = setTimeout(() => {
                    const streamCameras = new Set();
                    visibleCameras.forEach(camera => {
                        if (camera && camera.isOnline) {
                            console.log('Auto-loading stream for:', camera.name);
                            streamCameras.add(camera._id);
                        }
                    });
                    setAutoLoadCameras(streamCameras);
                    console.log('Set auto-load for cameras:', Array.from(streamCameras));
                }, 1500);
                
                return () => clearTimeout(streamTimer);
            }
        }, 100);
        
        return () => clearTimeout(loadTimer);
    }, [isFullscreen, fullscreenPage, fullscreenGridLayout, currentView]);

    // Handle navigation from Dashboard map click
    useEffect(() => {
        if (location.state?.selectedCamera && location.state?.openFullscreen && availableCameras.length > 0) {
            const camera = availableCameras.find(c => c._id === location.state.selectedCamera._id);
            if (camera) {
                // Set the camera as selected and open in fullscreen modal
                setFullscreenCamera(camera);
                setShowFullscreenModal(true);
            }
            // Clear the navigation state to prevent reopening on subsequent renders
            window.history.replaceState({}, document.title);
        }
    }, [location.state, availableCameras]);

    const fetchCameras = async () => {
        try {
            setLoading(true);
            // Fetch all cameras with a high limit to get all assigned cameras
            const response = await api.get('/cameras/mine?limit=1000');
            const allCameras = response.data.data.cameras;
            console.log('CameraMonitoring - Total cameras received:', allCameras.length);
            console.log('CameraMonitoring - Camera names:', allCameras.map(c => c.name));
            setAvailableCameras(allCameras);
            
            // Extract dynamic filter options
            extractFilterOptions(allCameras);
            
            setFilteredCameras(allCameras);
        } catch (error) {
            console.error('Error fetching cameras:', error);
        } finally {
            setLoading(false);
        }
    };

    // Extract dynamic filter options from camera data
    const extractFilterOptions = (cameras) => {
        // Extract unique locations
        const locations = new Set(['ALL']);
        const categories = new Set();

        cameras.forEach(camera => {
            // Add only exact_location data
            if (camera.location && camera.location.exactLocation && camera.location.exactLocation !== 'general') {
                locations.add(camera.location.exactLocation.charAt(0).toUpperCase() + camera.location.exactLocation.slice(1));
            }

            // Extract categories from name and description
            const nameWords = camera.name?.toLowerCase().split(/[\s_-]+/) || [];
            const descWords = camera.description?.toLowerCase().split(/[\s_-]+/) || [];
            const allWords = [...nameWords, ...descWords];

            // Common category keywords
            const categoryKeywords = [
                'office', 'store', 'stores', 'mess', 'parking', 'quarry', 'siding', 
                'entrance', 'exit', 'gate', 'building', 'warehouse', 'security',
                'front', 'back', 'side', 'main', 'lobby', 'reception', 'corridor',
                'stair', 'stairs', 'elevator', 'lift', 'roof', 'basement', 'floor'
            ];

            allWords.forEach(word => {
                categoryKeywords.forEach(keyword => {
                    if (word.includes(keyword) || keyword.includes(word)) {
                        categories.add(keyword.charAt(0).toUpperCase() + keyword.slice(1));
                    }
                });
            });
        });

        setLocationOptions(Array.from(locations));
        setCategoryOptions(Array.from(categories).slice(0, 8)); // Limit to first 8 categories
    };

    // Dashboard filter functions
    const handleLocationToggle = (location) => {
        if (location === 'ALL') {
            setSelectedLocations(['ALL']);
        } else {
            const newSelection = selectedLocations.includes('ALL')
                ? [location]
                : selectedLocations.includes(location)
                    ? selectedLocations.filter(l => l !== location)
                    : [...selectedLocations.filter(l => l !== 'ALL'), location];
            
            setSelectedLocations(newSelection.length === 0 ? ['ALL'] : newSelection);
        }
        setCurrentPage(0); // Reset to first page
    };

    const handleCategoryToggle = (category) => {
        setSelectedCategories(prev =>
            prev.includes(category)
                ? prev.filter(c => c !== category)
                : [...prev, category]
        );
        setCurrentPage(0); // Reset to first page
    };

    const getDashboardFilteredCameras = () => {
        let filtered = availableCameras;

        // Filter by current view
        if (currentView === 'favourites') {
            filtered = availableCameras.filter(camera => preferences.favorites.includes(camera._id));
        } else if (currentView === 'custom') {
            // For custom view, use screen cameras if a screen is selected
            const activeScreen = getActiveScreen();
            if (activeScreen && screenCameras.length > 0) {
                // Get cameras from the active screen layout
                const screenCameraIds = screenCameras.map(sc => sc._id || sc.cameraId);
                filtered = availableCameras.filter(camera => screenCameraIds.includes(camera._id));
            } else {
                // No screen selected, show empty state
                filtered = [];
            }
        }

        // Filter by exact location only
        if (!selectedLocations.includes('ALL')) {
            filtered = filtered.filter(camera => {
                return selectedLocations.some(loc => {
                    const locLower = loc.toLowerCase();
                    return camera.location?.exactLocation?.toLowerCase().includes(locLower);
                });
            });
        }

        // Filter by online status
        if (onlineStatus === 'online') {
            filtered = filtered.filter(camera => camera.isOnline);
        } else if (onlineStatus === 'offline') {
            filtered = filtered.filter(camera => !camera.isOnline);
        }

        // Filter by categories
        if (selectedCategories.length > 0) {
            filtered = filtered.filter(camera => {
                return selectedCategories.some(cat => {
                    const catLower = cat.toLowerCase();
                    return (
                        camera.name?.toLowerCase().includes(catLower) ||
                        camera.description?.toLowerCase().includes(catLower) ||
                        camera.location?.exactLocation?.toLowerCase().includes(catLower)
                    );
                });
            });
        }

        return filtered;
    };

    // Add camera to favourites
    const handleAddToFavourites = (camera) => {
        console.log('handleAddToFavourites called with camera:', camera);
        toggleFavorite(camera._id);
        toast.success(`${camera.name} added to favourites`);
        setViewMode('favorites'); // Switch to favorites view
    };

    // Remove camera from favourites
    const handleRemoveFromFavourites = (cameraId) => {
        console.log('handleRemoveFromFavourites called with cameraId:', cameraId);
        toggleFavorite(cameraId);
        const camera = availableCameras.find(c => c._id === cameraId);
        if (camera) {
            toast.success(`${camera.name} removed from favourites`);
        }
    };

    // Handle camera card click to open fullscreen
    const handleCameraCardClick = (camera) => {
        setPreviousViewMode(currentView);
        setFullscreenCamera(camera);
        setShowFullscreenModal(true);
    };

    // Close fullscreen modal
    const handleCloseFullscreen = () => {
        setShowFullscreenModal(false);
        setFullscreenCamera(null);
        
        // Restore previous view mode if it was saved (e.g., from map view)
        if (previousViewMode) {
            setCurrentView(previousViewMode);
            setPreviousViewMode(null);
        }
    };

    const getDashboardGridDimensions = () => {
        switch (dashboardGridLayout) {
            case '1x1': return { rows: 1, cols: 1 };
            case '2x2': return { rows: 2, cols: 2 };
            case '3x3': return { rows: 3, cols: 3 };
            case '4x4': return { rows: 4, cols: 4 };
            case '5x5': return { rows: 5, cols: 5 };
            default: return { rows: 3, cols: 3 };
        }
    };

    const getFullscreenGridDimensions = () => {
        switch (fullscreenGridLayout) {
            case '1x1': return { rows: 1, cols: 1 };
            case '2x2': return { rows: 2, cols: 2 };
            case '3x3': return { rows: 3, cols: 3 };
            case '4x4': return { rows: 4, cols: 4 };
            default: return { rows: 3, cols: 3 };
        }
    };

    // Get responsive text sizes based on grid layout
    const getTextSizes = (layout) => {
        switch (layout) {
            case '1x1':
                return {
                    cameraName: 'text-2xl font-bold',
                    location: 'text-lg',
                    status: 'text-lg font-bold',
                    badge: 'text-sm px-3 py-1',
                    controls: 'h-6 w-6 p-3',
                    statusDot: 'w-4 h-4',
                    overlay: 'p-1.5'
                };
            case '2x2':
                return {
                    cameraName: 'text-lg font-bold',
                    location: 'text-sm',
                    status: 'text-sm font-bold',
                    badge: 'text-xs px-2 py-1',
                    controls: 'h-5 w-5 p-2.5',
                    statusDot: 'w-3 h-3',
                    overlay: 'p-1'
                };
            case '3x3':
                return {
                    cameraName: 'text-base font-bold',
                    location: 'text-sm',
                    status: 'text-sm font-bold',
                    badge: 'text-xs px-2 py-1',
                    controls: 'h-4 w-4 p-2',
                    statusDot: 'w-3 h-3',
                    overlay: 'p-1.5'
                };
            case '4x4':
                return {
                    cameraName: 'text-sm font-semibold',
                    location: 'text-xs',
                    status: 'text-xs font-semibold',
                    badge: 'text-xs px-1.5 py-0.5',
                    controls: 'h-3 w-3 p-1.5',
                    statusDot: 'w-2 h-2',
                    overlay: 'p-1'
                };
            case '5x5':
                return {
                    cameraName: 'text-xs font-semibold',
                    location: 'text-xs',
                    status: 'text-xs font-semibold',
                    badge: 'text-xs px-1 py-0.5',
                    controls: 'h-3 w-3 p-1',
                    statusDot: 'w-2 h-2',
                    overlay: 'p-1'
                };
            default:
                return {
                    cameraName: 'text-base font-bold',
                    location: 'text-sm',
                    status: 'text-sm font-bold',
                    badge: 'text-xs px-2 py-1',
                    controls: 'h-4 w-4 p-2',
                    statusDot: 'w-3 h-3',
                    overlay: 'p-1.5'
                };
        }
    };

    // Simplified screen management for non-tech users
    const handleScreenChange = (screenId) => {
        setActiveScreen(screenId);
        const screen = preferences.screens?.screenConfigs?.[screenId];
        if (screen) {
            loadScreenCameras(screen);
        }
    };

    const handleEditScreen = (screenId) => {
        setEditingScreenId(screenId);
        setShowScreenBuilder(true);
        setShowScreenManager(false);
    };

    const handleViewScreen = (screenId) => {
        handleScreenChange(screenId);
        setShowScreenManager(false);
    };

    const handleCreateFirstScreen = () => {
        // Create a simple default screen for non-tech users
        const screenConfig = {
            name: 'Main View',
            grid: { columns: 2, rows: 2 },
            cameras: availableCameras.slice(0, 4).map((camera, index) => ({
                cameraId: camera._id,
                position: { 
                    row: Math.floor(index / 2) + 1, 
                    col: (index % 2) + 1, 
                    rowSpan: 1, 
                    colSpan: 1 
                },
                size: 'normal'
            }))
        };
        
        const screenId = createScreen(screenConfig);
        setViewMode('screen');
        toast.success('Main View created! You can now organize your cameras.');
    };

    const getCurrentCameras = () => {
        if (viewMode === 'live') {
            return showAllCameras ? availableCameras : selectedCameras;
        }
        return screenCameras;
    };

    const handleCameraToggle = (camera) => {
        // Add to recent cameras
        addRecentCamera(camera._id);
        
        if (selectedCameras.find(c => c._id === camera._id)) {
            // Remove camera if already selected
            setSelectedCameras(selectedCameras.filter(c => c._id !== camera._id));
        } else {
            // Add camera - dynamic grid handles any number
            setSelectedCameras([...selectedCameras, camera]);
        }
    };

    const removeCamera = (cameraId) => {
        setSelectedCameras(selectedCameras.filter(c => c._id !== cameraId));
    };

    const handleDoubleClick = (camera) => {
        // Toggle picture-in-picture
        setPipCamera(camera);
        setShowPip(true);
    };

    const toggleFullscreen = () => {
        console.log('toggleFullscreen called');
        console.log('Current isFullscreen state:', isFullscreen);
        console.log('Current fullscreen element:', document.fullscreenElement);
        console.log('fullscreenRef.current:', fullscreenRef.current);
        
        if (!isFullscreen && !document.fullscreenElement) {
            // Enter fullscreen
            if (!fullscreenRef.current) {
                console.error('Fullscreen ref is not available');
                toast.error('Unable to enter fullscreen mode - ref not available');
                return;
            }
            
            console.log('Attempting to enter fullscreen...');
            const elem = fullscreenRef.current;
            
            if (elem.requestFullscreen) {
                elem.requestFullscreen()
                    .then(() => {
                        console.log('Successfully entered fullscreen (standard)');
                        setIsFullscreen(true);
                    })
                    .catch(err => {
                        console.error('Fullscreen error:', err);
                        toast.error(`Fullscreen failed: ${err.message}`);
                    });
            } else if (elem.webkitRequestFullscreen) {
                // Safari
                elem.webkitRequestFullscreen();
                setIsFullscreen(true);
                console.log('Entered fullscreen (webkit)');
            } else if (elem.mozRequestFullScreen) {
                // Firefox
                elem.mozRequestFullScreen();
                setIsFullscreen(true);
                console.log('Entered fullscreen (moz)');
            } else if (elem.msRequestFullscreen) {
                // IE/Edge
                elem.msRequestFullscreen();
                setIsFullscreen(true);
                console.log('Entered fullscreen (ms)');
            } else {
                console.error('Fullscreen API not supported');
                toast.error('Fullscreen not supported in this browser');
            }
        } else {
            // Exit fullscreen
            console.log('Attempting to exit fullscreen...');
            
            if (document.exitFullscreen) {
                document.exitFullscreen()
                    .then(() => {
                        console.log('Successfully exited fullscreen');
                        setIsFullscreen(false);
                    })
                    .catch(err => {
                        console.error('Exit fullscreen error:', err);
                    });
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
                setIsFullscreen(false);
            } else if (document.mozCancelFullScreen) {
                document.mozCancelFullScreen();
                setIsFullscreen(false);
            } else if (document.msExitFullscreen) {
                document.msExitFullscreen();
                setIsFullscreen(false);
            }
        }
    };



    // Get cameras for current view
    const getDisplayCameras = () => {
        if (viewMode === 'live') {
            return showAllCameras ? availableCameras : selectedCameras;
        } else if (viewMode === 'screen') {
            return screenCameras;
        } else if (viewMode === 'favorites') {
            return availableCameras.filter(camera => preferences.favorites.includes(camera._id));
        }
        return availableCameras;
    };

    // Simple grid layout for non-tech users (like Hikvision)
    const getGridLayout = () => {
        const cameras = getDisplayCameras();
        const totalCameras = cameras.length;
        
        if (gridSize === 1) {
            return { columns: 1, rows: 1 };
        } else if (gridSize === 4) {
            return { columns: 2, rows: 2 };
        } else if (gridSize === 9) {
            return { columns: 3, rows: 3 };
        } else if (gridSize === 16) {
            return { columns: 4, rows: 4 };
        }
        
        // Auto-calculate based on camera count
        const sqrt = Math.ceil(Math.sqrt(totalCameras));
        return { columns: sqrt, rows: sqrt };
    };

    if (!hasPermission('cameras', 'read')) {
        return (
            <div className="space-y-8 max-w-7xl mx-auto px-2 md:px-6 py-6">
                <div className="bg-white rounded-xl shadow p-4">
                    <div className="text-center py-12">
                        <Camera className="h-16 w-16 text-indigo-400 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h3>
                        <p className="text-gray-600">You don't have permission to view cameras.</p>
                    </div>
                </div>
            </div>
        );
    }

    // Simplified Live View Component for non-tech users
    const LiveViewDisplay = ({ cameras, gridLayout, hideControls = false, isFullscreen = false, showCameraModal = false }) => {
        if (!cameras || cameras.length === 0) {
            return (
                <div className="h-full flex items-center justify-center text-gray-500">
                    <div className="text-center">
                        <Video className="h-16 w-16 mx-auto mb-6 text-gray-400" />
                        <p className="text-xl font-medium mb-3 text-white">No Cameras Available</p>
                        <p className="text-sm mb-4 text-gray-300">No cameras to display in this view</p>
                    </div>
                </div>
            );
        }

        return (
            <div className="h-full flex flex-col">
                {!hideControls && (
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                                Live View
                            </h3>
                            <p className="text-sm text-gray-500">
                                {gridLayout.columns}×{gridLayout.rows} Layout • {cameras.length} cameras
                            </p>
                        </div>
                        <div className="flex items-center space-x-2">
                            {/* Simple grid size controls like Hikvision */}
                            <div className="flex items-center space-x-2">
                                <span className="text-xs text-gray-500">Layout:</span>
                                <div className="flex bg-gray-100 rounded-lg p-1">
                                {[1, 4, 9, 16].map((size) => (
                                    <button
                                        key={size}
                                        onClick={() => setGridSize(size)}
                                        className={`px-3 py-1 text-sm rounded-md transition-colors ${
                                            gridSize === size 
                                                ? 'bg-white text-gray-900 shadow-sm' 
                                                : 'text-gray-600 hover:text-gray-900'
                                        }`}
                                    >
                                        {size === 1 ? '1' : size === 4 ? '2×2' : size === 9 ? '3×3' : '4×4'}
                                    </button>
                                ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="flex-1">
                    <div 
                        className="grid gap-1 w-full h-full p-2 rounded-lg bg-gray-900"
                        style={{
                            gridTemplateColumns: `repeat(${gridLayout.columns}, 1fr)`,
                            gridTemplateRows: `repeat(${gridLayout.rows}, 1fr)`,
                            gap: '4px'
                        }}
                    >
                        {Array.from({ length: gridLayout.rows }).map((_, rowIndex) =>
                            Array.from({ length: gridLayout.columns }).map((_, colIndex) => {
                                const cameraIndex = rowIndex * gridLayout.columns + colIndex;
                                const camera = cameras[cameraIndex];
                                
                                return (
                                    <div
                                        key={`${rowIndex}-${colIndex}`}
                                        className="relative aspect-video bg-gray-800 rounded-lg overflow-hidden"
                                    >
                                        {camera ? (
                                            <>
                                                {(readyStreamCameras.has(camera._id) || autoLoadCameras.has(camera._id)) ? (
                                                        <SimpleCameraStream 
                                                            camera={camera} 
                                                            onStreamReady={handleStreamReady}
                                                            onStreamError={handleStreamError}
                                                            hideControls={true}
                                                        />
                                                    ) : (
                                                        <>
                                                            {/* Show GIF while stream is loading */}
                                                            <CachedSnapshot 
                                                                cameraId={camera._id}
                                                                refreshKey={thumbnailKeys[camera._id] || 0}
                                                                alt={`${camera.name} snapshot`}
                                                                className="absolute inset-0 w-full h-full"
                                                                showLoadingIndicator={loadingStreamCameras.has(camera._id)}
                                                                showCacheIndicator={false}
                                                                options={{
                                                                    gifTimeout: 15000,
                                                                    jpegTimeout: 8000,
                                                                    gifDuration: 3,
                                                                    gifFps: 12
                                                                }}
                                                            />
                                                            {/* Loading overlay for stream */}
                                                            {loadingStreamCameras.has(camera._id) && (
                                                                <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center">
                                                                    <div className="bg-white bg-opacity-90 rounded-lg px-3 py-2">
                                                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mx-auto mb-1"></div>
                                                                        <p className="text-xs text-gray-600">Loading stream...</p>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                
                                                {/* Refresh button */}
                                                <button
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        refreshThumbnail(camera._id);
                                                        toast.success(`Refreshing ${camera.name}`, { duration: 1500 });
                                                    }}
                                                    className="hidden"
                                                    title="Refresh Camera"
                                                >
                                                    <RefreshCw className="h-3 w-3" />
                                                </button>

                                                {/* Audio toggle button */}
                                                <button
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        toggleAudio(camera._id);
                                                    }}
                                                    className="hidden"
                                                    title={audioStates[camera._id] ? 'Disable Audio' : 'Enable Audio'}
                                                >
                                                    {(audioStates[camera._id] || false) ? 
                                                        <Volume2 className="h-3 w-3" /> : 
                                                        <VolumeX className="h-3 w-3" />
                                                    }
                                                </button>

                                                {/* Favorite button */}
                                                <button
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        if (preferences.favorites.includes(camera._id)) {
                                                            handleRemoveFromFavourites(camera._id);
                                                        } else {
                                                            handleAddToFavourites(camera);
                                                        }
                                                    }}
                                                    className="absolute top-2 right-2 bg-black bg-opacity-70 text-white text-xs p-2 rounded hover:bg-opacity-90 transition-all opacity-0 group-hover:opacity-100 z-10"
                                                    title={preferences.favorites.includes(camera._id) ? 'Remove from favorites' : 'Add to favorites'}
                                                >
                                                    <Heart className={`h-3 w-3 ${preferences.favorites.includes(camera._id) ? 'fill-current text-red-500' : ''}`} />
                                                </button>

                                                {/* Manual mode toggle button */}
                                                <button
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        toggleCameraMode(camera._id);
                                                    }}
                                                    className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded hover:bg-opacity-90 transition-all opacity-0 group-hover:opacity-100 z-10"
                                                    title={readyStreamCameras.has(camera._id) ? 'Switch to GIF' : 'Force Stream'}
                                                >
                                                    {readyStreamCameras.has(camera._id) ? '📸' : '▶️'}
                                                </button>

                                            </>
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center bg-gray-800 text-gray-400">
                                                <div className="text-center">
                                                    <div className="text-xs opacity-50">Empty</div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // Screen-based display component (simplified for non-tech users)
    const ScreenBasedDisplay = ({ 
        activeScreen, 
        screenCameras, 
        availableCameras, 
        onEditScreen, 
        onCreateFirstScreen, 
        hasScreens 
    }) => {
        if (!hasScreens) {
            return (
                <div className="h-full flex items-center justify-center text-gray-500">
                    <div className="text-center">
                        <Layout className="h-16 w-16 mx-auto mb-6 text-gray-400" />
                        <p className="text-xl font-medium mb-3">Create Your First View</p>
                        <p className="text-sm mb-4 max-w-md">Organize your cameras into custom layouts for easier monitoring</p>
                        <button
                            onClick={onCreateFirstScreen}
                            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 to-purple-800 text-white rounded-lg font-medium transition-colors"
                            disabled={availableCameras.length === 0}
                        >
                            <Plus className="h-4 w-4 inline mr-2" />
                            Create Main View
                        </button>
                        {availableCameras.length === 0 && (
                            <p className="text-xs text-gray-400 mt-2">No cameras available</p>
                        )}
                    </div>
                </div>
            );
        }

        if (!activeScreen) {
            return (
                <div className="h-full flex items-center justify-center text-gray-500">
                    <div className="text-center">
                        <Monitor className="h-16 w-16 mx-auto mb-6 text-gray-400" />
                        <p className="text-xl font-medium mb-3">Select a View</p>
                        <p className="text-sm mb-4">Choose a view from the dropdown above to start monitoring</p>
                    </div>
                </div>
            );
        }

        return (
            <div className="h-full flex flex-col">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">{activeScreen.name}</h3>
                        <p className="text-sm text-gray-500">
                            {activeScreen.grid.columns}×{activeScreen.grid.rows} Layout • {screenCameras.length} cameras
                        </p>
                    </div>
                    <button
                        onClick={() => onEditScreen(activeScreen.id)}
                        className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors"
                    >
                        <Settings className="h-4 w-4 inline mr-1" />
                        Edit Layout
                    </button>
                </div>

                <div className="flex-1">
                    {screenCameras.length > 0 ? (
                        <div 
                            className="grid gap-1 w-full h-full p-2 rounded-lg bg-gray-900"
                            style={{
                                gridTemplateColumns: `repeat(${activeScreen.grid.columns}, 1fr)`,
                                gridTemplateRows: `repeat(${activeScreen.grid.rows}, 1fr)`,
                                gap: `${activeScreen.grid.cellGaps || 4}px`
                            }}
                        >
                            {Array.from({ length: activeScreen.grid.rows }).map((_, rowIndex) =>
                                Array.from({ length: activeScreen.grid.columns }).map((_, colIndex) => {
                                    const row = rowIndex + 1;
                                    const col = colIndex + 1;
                                    const camera = screenCameras.find(c => 
                                        c.position?.row === row && c.position?.col === col
                                    );
                                    
                                    return (
                                        <div
                                            key={`${row}-${col}`}
                                            className="relative aspect-video bg-gray-800 rounded-lg overflow-hidden"
                                        >
                                            {camera ? (
                                                <>
                                                    <CachedSnapshot 
                                                        cameraId={camera._id}
                                                        refreshKey={thumbnailKeys[camera._id] || 0}
                                                        alt={`${camera.name} snapshot`}
                                                        className="absolute inset-0 w-full h-full"
                                                        showLoadingIndicator={true}
                                                    />
                                                </>
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center bg-gray-800 text-gray-400">
                                                    <div className="text-center">
                                                        <div className="text-xs opacity-50">Empty</div>
                                                        <div className="text-xs opacity-30">{row},{col}</div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    ) : (
                        <div className="h-full flex items-center justify-center text-gray-500">
                            <div className="text-center">
                                <Grid className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                                <p className="text-lg font-medium mb-2">Empty Layout</p>
                                <p className="text-sm mb-4">This layout has no cameras configured</p>
                                <button
                                    onClick={() => onEditScreen(activeScreen.id)}
                                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors"
                                >
                                    Add Cameras
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div
            ref={fullscreenRef}
            className={`${isFullscreen ? 'fixed inset-0 z-50 bg-black' : ''} ${isFullscreen ? 'flex flex-col' : ''}`}
        >
            {!isFullscreen ? (
                showDashboardView ? (
                    // New Dashboard UI Layout
                    <div className="h-screen bg-gray-50 flex flex-col">
                        {/* Minimal Top Bar */}
                        <div className="bg-white border-b border-gray-200">
                            <div className="px-6 py-3">
                                <div className="flex items-center justify-between">
                                    {/* Simple view toggle */}
                                    <div className="flex items-center space-x-4">
                                        <button
                                            onClick={() => {
                                                setCurrentView('live');
                                                setCurrentPage(0);
                                            }}
                                            className={`text-sm font-medium transition-colors ${
                                                currentView === 'live'
                                                    ? 'text-indigo-700 border-b-2 border-indigo-600 pb-1'
                                                    : 'text-gray-500 hover:text-indigo-600'
                                            }`}
                                        >
                                            Live
                                        </button>
                                        <button
                                            onClick={() => {
                                                setCurrentView('favourites');
                                                setCurrentPage(0);
                                            }}
                                            className={`text-sm font-medium transition-colors ${
                                                currentView === 'favourites'
                                                    ? 'text-indigo-700 border-b-2 border-indigo-600 pb-1'
                                                    : 'text-gray-500 hover:text-indigo-600'
                                            }`}
                                        >
                                            Favourites
                                        </button>
                                        <button
                                            onClick={() => {
                                                setCurrentView('custom');
                                                setCurrentPage(0);
                                            }}
                                            className={`text-sm font-medium transition-colors ${
                                                currentView === 'custom'
                                                    ? 'text-indigo-700 border-b-2 border-indigo-600 pb-1'
                                                    : 'text-gray-500 hover:text-indigo-600'
                                            }`}
                                        >
                                            Custom
                                        </button>
                                        <button
                                            onClick={() => {
                                                setCurrentView('map');
                                                setCurrentPage(0);
                                            }}
                                            className={`text-sm font-medium transition-colors ${
                                                currentView === 'map'
                                                    ? 'text-indigo-700 border-b-2 border-indigo-600 pb-1'
                                                    : 'text-gray-500 hover:text-indigo-600'
                                            }`}
                                        >
                                            Map
                                        </button>
                                    </div>

                                    {/* Minimal controls */}
                                    <div className="flex items-center space-x-4">
                                        {currentView === 'custom' && (
                                            <div className="flex items-center space-x-2">
                                                {getScreens().length > 0 ? (
                                                    <>
                                                        {/* Screen Selector Dropdown */}
                                                        <select
                                                            value={preferences.screens?.activeScreenId || ''}
                                                            onChange={(e) => handleScreenChange(e.target.value)}
                                                            className="text-sm border border-gray-300 rounded-md px-3 py-1 bg-white text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                        >
                                                            <option value="">Select Screen...</option>
                                                            {getScreens().map(screen => (
                                                                <option key={screen.id} value={screen.id}>
                                                                    {screen.name} ({screen.grid.columns}×{screen.grid.rows})
                                                                </option>
                                                            ))}
                                                        </select>
                                                        {/* Quick Actions */}
                                                        <button
                                                            onClick={() => setEditingScreenId('new') || setShowScreenBuilder(true)}
                                                            className="flex items-center space-x-1 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors text-sm"
                                                            title="Add New Screen"
                                                        >
                                                            <Plus className="h-3 w-3" />
                                                            <span>New</span>
                                                        </button>
                                                        {preferences.screens?.activeScreenId && (
                                                            <button
                                                                onClick={() => handleEditScreen(preferences.screens.activeScreenId)}
                                                                className="flex items-center space-x-1 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors text-sm"
                                                                title="Edit Current Screen"
                                                            >
                                                                <Settings className="h-3 w-3" />
                                                                <span>Edit</span>
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={() => setShowScreenManager(true)}
                                                            className="flex items-center space-x-1 px-3 py-1.5 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors text-sm"
                                                            title="Manage All Screens"
                                                        >
                                                            <Layout className="h-3 w-3" />
                                                            <span>Manage</span>
                                                        </button>
                                                    </>
                                                ) : (
                                                    <button
                                                        onClick={handleCreateFirstScreen}
                                                        className="flex items-center space-x-2 px-3 py-1.5 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 to-purple-800 text-white rounded-lg font-medium transition-colors text-sm"
                                                        disabled={availableCameras.length === 0}
                                                    >
                                                        <Plus className="h-3 w-3" />
                                                        <span>Create First Screen</span>
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                        <button
                                            onClick={() => {
                                                console.log('Fullscreen button clicked');
                                                console.log('Current view:', currentView);
                                                console.log('Current page:', currentPage);
                                                
                                                if (!document.fullscreenElement) {
                                                    // Try the component ref first
                                                    const elem = fullscreenRef.current;
                                                    if (elem && elem.requestFullscreen) {
                                                        elem.requestFullscreen().then(() => {
                                                            setIsFullscreen(true);
                                                            setFullscreenPage(0); // Reset to first page
                                                            console.log('Entered fullscreen mode with ref');
                                                        }).catch(err => {
                                                            console.error('Fullscreen ref error:', err);
                                                            // Fallback to document element
                                                            const docElem = document.documentElement;
                                                            if (docElem.requestFullscreen) {
                                                                docElem.requestFullscreen().then(() => {
                                                                    setIsFullscreen(true);
                                                                    console.log('Entered fullscreen mode with document');
                                                                }).catch(err2 => {
                                                                    console.error('Document fullscreen error:', err2);
                                                                    toast.error('Could not enter fullscreen mode');
                                                                });
                                                            }
                                                        });
                                                    } else if (elem && elem.webkitRequestFullscreen) {
                                                        elem.webkitRequestFullscreen();
                                                        setIsFullscreen(true);
                                                    } else {
                                                        // Fallback to document
                                                        const docElem = document.documentElement;
                                                        if (docElem.requestFullscreen) {
                                                            docElem.requestFullscreen().then(() => {
                                                                setIsFullscreen(true);
                                                            });
                                                        }
                                                    }
                                                } else {
                                                    document.exitFullscreen();
                                                    setIsFullscreen(false);
                                                }
                                            }}
                                            className="p-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                                            title="Toggle Fullscreen"
                                        >
                                            <Maximize2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Minimal Filter Bar */}
                        <div className="bg-gray-50 border-b border-gray-200">
                            <div className="px-6 py-2">
                                <div className="flex items-center justify-between">
                                    {/* Essential filters only - Hide in map view except location */}
                                    <div className="flex items-center space-x-3 text-sm">
                                        {currentView !== 'map' && (
                                            <select
                                                value={onlineStatus}
                                                onChange={(e) => {
                                                    setOnlineStatus(e.target.value);
                                                    setCurrentPage(0);
                                                }}
                                                className="border-0 bg-transparent text-gray-600 text-sm focus:ring-0 cursor-pointer"
                                            >
                                                <option value="all">All Cameras</option>
                                                <option value="online">Online Only</option>
                                                <option value="offline">Offline Only</option>
                                            </select>
                                        )}
                                        
                                        {currentView !== 'map' && locationOptions.length > 1 && (
                                            <select
                                                value={selectedLocations.includes('ALL') ? 'ALL' : selectedLocations[0] || 'ALL'}
                                                onChange={(e) => {
                                                    setSelectedLocations([e.target.value]);
                                                    setCurrentPage(0);
                                                }}
                                                className="border-0 bg-transparent text-gray-600 text-sm focus:ring-0 cursor-pointer"
                                            >
                                                <option value="ALL">All Locations</option>
                                                {locationOptions.filter(loc => loc !== 'ALL').map(location => (
                                                    <option key={location} value={location}>{location}</option>
                                                ))}
                                            </select>
                                        )}
                                    </div>

                                    {/* Simple grid controls - Hide in map and custom view */}
                                    {currentView !== 'map' && currentView !== 'custom' && (
                                        <div className="flex items-center space-x-2">
                                            {['2x2', '3x3', '4x4'].map(layout => (
                                                <button
                                                    key={layout}
                                                    onClick={() => {
                                                        setDashboardGridLayout(layout);
                                                        setCurrentPage(0);
                                                    }}
                                                    className={`px-2 py-1 text-xs transition-colors ${
                                                        dashboardGridLayout === layout
                                                            ? 'text-indigo-700 font-medium'
                                                            : 'text-gray-500 hover:text-indigo-600'
                                                    }`}
                                                >
                                                        {layout}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Main Camera Grid */}
                        <div className="flex-1 bg-gray-900 p-2 relative">
                            {(() => {
                                const filteredCameras = getDashboardFilteredCameras();
                                const { rows, cols } = getDashboardGridDimensions();
                                const textSizes = getTextSizes(dashboardGridLayout);
                                const camerasPerPage = rows * cols;
                                const totalPages = Math.ceil(filteredCameras.length / camerasPerPage);
                                const startIndex = currentPage * camerasPerPage;
                                const endIndex = startIndex + camerasPerPage;
                                const currentPageCameras = filteredCameras.slice(startIndex, endIndex);

                                // Show loading state first
                                if (loading) {
                                    return (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <div className="text-center text-white">
                                                <div className="animate-spin h-12 w-12 border-b-2 border-indigo-400 mx-auto mb-4 rounded-full"></div>
                                                <h3 className="text-xl font-semibold mb-2">Loading Cameras...</h3>
                                                <p className="text-indigo-200">Please wait while we fetch your cameras</p>
                                            </div>
                                        </div>
                                    );
                                }

                                if (filteredCameras.length === 0) {
                                    return (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <div className="text-center text-white">
                                                <Video className="h-16 w-16 mx-auto mb-4 text-indigo-300" />
                                                <h3 className="text-xl font-semibold mb-2">No Cameras Found</h3>
                                                <p className="text-blue-200 mb-4">
                                                    {currentView === 'favourites' 
                                                        ? 'You haven\'t added any cameras to your favourites yet.'
                                                        : currentView === 'custom' && !getActiveScreen()
                                                        ? 'Please select a custom screen to view cameras.'
                                                        : selectedLocations.length > 1 || selectedCategories.length > 0 || onlineStatus !== 'all'
                                                        ? 'No cameras match your current filters. Try adjusting your filters.'
                                                        : 'No cameras are currently available.'
                                                    }
                                                </p>
                                                {(currentView !== 'custom' || !getActiveScreen()) && (
                                                    <button
                                                        onClick={() => {
                                                            setSelectedLocations(['ALL']);
                                                            setSelectedCategories([]);
                                                            setOnlineStatus('all');
                                                            setCurrentView('live');
                                                            setCurrentPage(0);
                                                        }}
                                                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors"
                                                    >
                                                        Clear All Filters
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                }

                                return (
                                    <>
                                        {currentView === 'custom' && getActiveScreen() ? (
                                            // Custom screen layout
                                            <div 
                                                className="grid gap-1 w-full h-full"
                                                style={{
                                                    gridTemplateColumns: `repeat(${getActiveScreen().grid.columns}, 1fr)`,
                                                    gridTemplateRows: `repeat(${getActiveScreen().grid.rows}, 1fr)`,
                                                }}
                                            >
                                                {Array.from({ length: getActiveScreen().grid.rows }).map((_, rowIndex) =>
                                                    Array.from({ length: getActiveScreen().grid.columns }).map((_, colIndex) => {
                                                        const row = rowIndex + 1;
                                                        const col = colIndex + 1;
                                                        const screenCamera = screenCameras.find(sc => 
                                                            sc.position?.row === row && sc.position?.col === col
                                                        );
                                                        const camera = screenCamera ? availableCameras.find(c => c._id === (screenCamera._id || screenCamera.cameraId)) : null;
                                                        
                                                        return (
                                                            <div
                                                                key={`${row}-${col}`}
                                                                className="group bg-gray-800 rounded-lg border border-gray-700 relative overflow-hidden hover:border-gray-600 transition-all duration-200 cursor-pointer"
                                                                onClick={() => camera && handleCameraCardClick(camera)}
                                                            >
                                                                {camera ? (
                                                                    <>
                                                                        <div className="relative w-full h-full">
                                                                            {autoLoadCameras.has(camera._id) ? (
                                                                                <>
                                                                                    {/* Show live stream (may be loading) */}
                                                                                    <SimpleCameraStream 
                                                                                        camera={camera}
                                                                                        onStreamReady={handleStreamReady}
                                                                                        onStreamError={handleStreamError}
                                                                                        className="absolute inset-0 w-full h-full"
                                                                                        hideControls={true}
                                                                                    />
                                                                                    
                                                                                    {/* Show GIF overlay while stream is loading */}
                                                                                    {!readyStreamCameras.has(camera._id) && (
                                                                                        <div className="absolute inset-0 w-full h-full">
                                                                                            <CachedSnapshot 
                                                                                                cameraId={camera._id}
                                                                                                refreshKey={thumbnailKeys[camera._id] || 0}
                                                                                                alt={`${camera.name} snapshot`}
                                                                                                className="w-full h-full"
                                                                                                showLoadingIndicator={false}
                                                                                            />
                                                                                        </div>
                                                                                    )}
                                                                                </>
                                                                            ) : (
                                                                                // Show only GIF thumbnail
                                                                                <CachedSnapshot 
                                                                                    cameraId={camera._id}
                                                                                    refreshKey={thumbnailKeys[camera._id] || 0}
                                                                                    alt={`${camera.name} snapshot`}
                                                                                    className="absolute inset-0 w-full h-full"
                                                                                    showLoadingIndicator={false}
                                                                                />
                                                                            )}
                                                                            
                                                                            {/* Favorite button */}
                                                                            <button
                                                                                onClick={(e) => {
                                                                                    e.preventDefault();
                                                                                    e.stopPropagation();
                                                                                    if (preferences.favorites.includes(camera._id)) {
                                                                                        handleRemoveFromFavourites(camera._id);
                                                                                    } else {
                                                                                        handleAddToFavourites(camera);
                                                                                    }
                                                                                }}
                                                                                className="absolute top-2 right-2 bg-black bg-opacity-70 text-white text-xs p-2 rounded hover:bg-opacity-90 transition-all opacity-0 group-hover:opacity-100 z-10"
                                                                                title={preferences.favorites.includes(camera._id) ? 'Remove from favorites' : 'Add to favorites'}
                                                                            >
                                                                                <Heart className={`h-3 w-3 ${preferences.favorites.includes(camera._id) ? 'fill-current text-red-500' : ''}`} />
                                                                            </button>

                                                                            {/* Manual mode toggle button */}
                                                                            <button
                                                                                onClick={(e) => {
                                                                                    e.preventDefault();
                                                                                    e.stopPropagation();
                                                                                    toggleCameraMode(camera._id);
                                                                                }}
                                                                                className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded hover:bg-opacity-90 transition-all opacity-0 group-hover:opacity-100 z-10"
                                                                                title={readyStreamCameras.has(camera._id) ? 'Switch to GIF' : 'Force Stream'}
                                                                            >
                                                                                {readyStreamCameras.has(camera._id) ? '📸' : '▶️'}
                                                                            </button>

                                                                            {/* Camera info with mode indicator */}
                                                                            <div className={`absolute bottom-0 left-0 right-0 bg-black/60 text-white ${textSizes.overlay}`}>
                                                                                <div className="flex items-center justify-between">
                                                                                    <div className="flex-1 min-w-0">
                                                                                        <h4 className={`${textSizes.cameraName} truncate`}>{camera.name}</h4>
                                                                                        <div className="flex items-center space-x-2 mt-1">
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className={`${textSizes.statusDot} rounded-full ${
                                                                                        readyStreamCameras.has(camera._id) ? 'bg-green-500' : 
                                                                                        autoLoadCameras.has(camera._id) ? 'bg-orange-500' : 'bg-gray-500'
                                                                                    } ml-2`} />
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </>
                                                                ) : (
                                                                    <div className="w-full h-full flex items-center justify-center bg-gray-700">
                                                                        <div className="text-gray-500 text-center">
                                                                            <Monitor className="h-8 w-8 mx-auto mb-2 opacity-40" />
                                                                            <p className="text-xs opacity-60">Empty</p>
                                                                            <p className="text-xs opacity-30">{row},{col}</p>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })
                                                )}
                                            </div>
                                        ) : currentView === 'map' ? (
                                            // Map View
                                            <div className="w-full h-full bg-white rounded-lg overflow-hidden">
                                                <GoogleMapsView
                                                    cameras={availableCameras}
                                                    onCameraClick={(camera) => {
                                                        setPreviousViewMode(currentView);
                                                        setFullscreenCamera(camera);
                                                        setShowFullscreenModal(true);
                                                    }}
                                                />
                                            </div>
                                        ) : (
                                            // Regular grid layout for Live and Favourites
                                            <div 
                                                className="grid gap-1 w-full h-full"
                                                style={{
                                                    gridTemplateColumns: `repeat(${cols}, 1fr)`,
                                                    gridTemplateRows: `repeat(${rows}, 1fr)`,
                                                }}
                                            >
                                                {Array.from({ length: rows * cols }).map((_, index) => {
                                                    const camera = currentPageCameras[index];
                                                    return (
                                                        <div
                                                            key={camera ? `${camera._id}-${camera.name}` : index}
                                                            className="group bg-gray-800 rounded-lg border border-gray-700 relative overflow-hidden hover:border-gray-600 transition-all duration-200 cursor-pointer"
                                                            onClick={() => camera && handleCameraCardClick(camera)}
                                                        >
                                                            {camera ? (
                                                                <>
                                                                    <div className="relative w-full h-full">
                                                                        {autoLoadCameras.has(camera._id) ? (
                                                                            <>
                                                                                {/* Show live stream (may be loading) */}
                                                                                <SimpleCameraStream 
                                                                                    camera={camera}
                                                                                    onStreamReady={handleStreamReady}
                                                                                    onStreamError={handleStreamError}
                                                                                    className="absolute inset-0 w-full h-full"
                                                                                    hideControls={true}
                                                                                />
                                                                                
                                                                                {/* Show GIF overlay while stream is loading */}
                                                                                {!readyStreamCameras.has(camera._id) && (
                                                                                    <div className="absolute inset-0 w-full h-full">
                                                                                        <CachedSnapshot 
                                                                                            cameraId={camera._id}
                                                                                            refreshKey={thumbnailKeys[camera._id] || 0}
                                                                                            alt={`${camera.name} snapshot`}
                                                                                            className="w-full h-full"
                                                                                            showLoadingIndicator={false}
                                                                                        />
                                                                                    </div>
                                                                                )}
                                                                            </>
                                                                        ) : (
                                                                            // Show only GIF thumbnail
                                                                            <CachedSnapshot 
                                                                                cameraId={camera._id}
                                                                                refreshKey={thumbnailKeys[camera._id] || 0}
                                                                                alt={`${camera.name} snapshot`}
                                                                                className="absolute inset-0 w-full h-full"
                                                                                showLoadingIndicator={false}
                                                                            />
                                                                        )}
                                                                        
                                                                        {/* Favorite button */}
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.preventDefault();
                                                                                e.stopPropagation();
                                                                                if (preferences.favorites.includes(camera._id)) {
                                                                                    handleRemoveFromFavourites(camera._id);
                                                                                } else {
                                                                                    handleAddToFavourites(camera);
                                                                                }
                                                                            }}
                                                                            className="absolute top-2 right-2 bg-black bg-opacity-70 text-white text-xs p-2 rounded hover:bg-opacity-90 transition-all opacity-0 group-hover:opacity-100 z-10"
                                                                            title={preferences.favorites.includes(camera._id) ? 'Remove from favorites' : 'Add to favorites'}
                                                                        >
                                                                            <Heart className={`h-3 w-3 ${preferences.favorites.includes(camera._id) ? 'fill-current text-red-500' : ''}`} />
                                                                        </button>

                                                                        {/* Manual mode toggle button */}
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.preventDefault();
                                                                                e.stopPropagation();
                                                                                toggleCameraMode(camera._id);
                                                                            }}
                                                                            className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded hover:bg-opacity-90 transition-all opacity-0 group-hover:opacity-100 z-10"
                                                                            title={readyStreamCameras.has(camera._id) ? 'Switch to GIF' : 'Force Stream'}
                                                                        >
                                                                            {readyStreamCameras.has(camera._id) ? '📸' : '▶️'}
                                                                        </button>

                                                                    {/* Minimal camera info with mode indicator */}
                                                                    <div className={`absolute bottom-0 left-0 right-0 bg-black/60 text-white ${textSizes.overlay}`}>
                                                                        <div className="flex items-center justify-between">
                                                                            <div className="flex-1 min-w-0">
                                                                                <h4 className={`${textSizes.cameraName} truncate`}>{camera.name}</h4>
                                                                                <div className="flex items-center space-x-2 mt-1">
                                                                                </div>
                                                                            </div>
                                                                            <div className={`${textSizes.statusDot} rounded-full ${
                                                                                readyStreamCameras.has(camera._id) ? 'bg-green-500' : 
                                                                                autoLoadCameras.has(camera._id) ? 'bg-orange-500' : 'bg-gray-500'
                                                                            } ml-2`} />
                                                                        </div>
                                                                    </div>
                                                                    </div>
                                                                </>
                                                            ) : (
                                                                <div className="w-full h-full flex items-center justify-center bg-gray-700">
                                                                    <div className="text-gray-500 text-center">
                                                                        <Monitor className="h-8 w-8 mx-auto mb-2 opacity-40" />
                                                                        <p className="text-xs opacity-60">Empty</p>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}


                                        {/* Minimal status */}
                                        <div className="absolute bottom-4 right-4 bg-black/50 text-white px-2 py-1 rounded text-xs">
                                            {filteredCameras.filter(c => c.isOnline).length}/{filteredCameras.length} online
                                        </div>
                                    </>
                                );
                            })()}
                        </div>

                        {/* Simple Navigation Bar - Hide in map view */}
                        {currentView !== 'map' && (
                            <div className="bg-gray-800 border-t border-gray-700">
                                {(() => {
                                    const filteredCameras = getDashboardFilteredCameras();
                                    const { rows, cols } = getDashboardGridDimensions();
                                    const camerasPerPage = rows * cols;
                                    const totalPages = Math.ceil(filteredCameras.length / camerasPerPage);
                                    const currentPageNumber = currentPage + 1;
                                    const hasNavigation = totalPages > 1;
                                    
                                    return (
                                        <div className="px-4 py-3">
                                            <div className="flex items-center justify-between">
                                                {/* Status Info */}
                                                <div className="text-white text-sm">
                                                    {filteredCameras.length} camera{filteredCameras.length !== 1 ? 's' : ''} 
                                                    {hasNavigation && ` • Page ${currentPageNumber} of ${totalPages}`}
                                                </div>
                                                
                                                {/* Simple Navigation - Only show if multiple pages */}
                                                {hasNavigation && (
                                                    <div className="flex items-center space-x-2">
                                                        <button
                                                            onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                                                            disabled={currentPage === 0}
                                                            className="px-3 py-1 bg-gray-700 text-white rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                                                        >
                                                            Previous
                                                        </button>
                                                        
                                                        <button
                                                            onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
                                                            disabled={currentPage >= totalPages - 1}
                                                            className="px-3 py-1 bg-gray-700 text-white rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                                                        >
                                                            Next
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>
                        )}
                    </div>
                ) : (
                    // Classic view (original layout)
                    <div className="space-y-8 max-w-7xl mx-auto px-2 md:px-6 py-6">
                        {/* Page Header */}
                        <div>
                            <div className="mb-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h1 className="text-3xl font-extrabold text-indigo-700 tracking-tight">Live Monitoring</h1>
                                        <p className="text-gray-600 mt-1">Monitor your security cameras in real-time</p>
                                        <div className="mt-2">
                                            <BreadcrumbNavigation />
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setShowDashboardView(true)}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                                    >
                                        Dashboard View
                                    </button>
                                </div>
                            </div>
                            
                            {/* Simplified Header Actions for non-tech users */}
                            <div className="flex flex-wrap items-center justify-center gap-3">
                                {/* View Mode Toggle - Simple and familiar */}
                                <div className="flex bg-gray-100 rounded-lg p-1">
                                    <button
                                        onClick={() => setViewMode('live')}
                                        className={`px-4 py-2 text-sm rounded-md transition-colors flex items-center space-x-2 ${
                                            viewMode === 'live' 
                                                ? 'bg-white text-gray-900 shadow-sm' 
                                                : 'text-gray-600 hover:text-gray-900'
                                        }`}
                                    >
                                        <Eye className="h-4 w-4" />
                                        <span>Live View</span>
                                    </button>
                                    <button
                                        onClick={() => setViewMode('favorites')}
                                        className={`px-4 py-2 text-sm rounded-md transition-colors flex items-center space-x-2 ${
                                            viewMode === 'favorites' 
                                                ? 'bg-white text-gray-900 shadow-sm' 
                                                : 'text-gray-600 hover:text-gray-900'
                                        }`}
                                    >
                                        <Heart className="h-4 w-4" />
                                        <span>Favorites</span>
                                    </button>
                                    <button
                                        onClick={() => setViewMode('map')}
                                        className={`px-4 py-2 text-sm rounded-md transition-colors flex items-center space-x-2 ${
                                            viewMode === 'map' 
                                                ? 'bg-white text-gray-900 shadow-sm' 
                                                : 'text-gray-600 hover:text-gray-900'
                                        }`}
                                    >
                                        <Map className="h-4 w-4" />
                                        <span>Map View</span>
                                    </button>
                                    {getScreens().length > 0 && (
                                        <button
                                            onClick={() => setViewMode('screen')}
                                            className={`px-4 py-2 text-sm rounded-md transition-colors flex items-center space-x-2 ${
                                                viewMode === 'screen' 
                                                    ? 'bg-white text-gray-900 shadow-sm' 
                                                    : 'text-gray-600 hover:text-gray-900'
                                            }`}
                                        >
                                            <Layout className="h-4 w-4" />
                                            <span>Custom Views</span>
                                        </button>
                                    )}
                                </div>

                                {/* Screen Management - Only show if screens exist */}
                                {getScreens().length > 0 && viewMode === 'screen' && (
                                    <ScreenThumbnailSelector
                                        onScreenChange={handleScreenChange}
                                        onEditScreen={handleEditScreen}
                                        onOpenScreenManager={() => setShowScreenManager(true)}
                                        availableCameras={availableCameras}
                                        isFullscreen={false}
                                    />
                                )}
                                
                                {/* Quick Actions */}
                                <button
                                    onClick={() => setShowScreenManager(true)}
                                    className="flex items-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white rounded-lg shadow-md transition text-sm font-medium"
                                    title="Manage Views"
                                >
                                    <Settings className="h-4 w-4" />
                                    <span className="hidden sm:inline">Manage Views</span>
                                </button>

                                <button
                                    onClick={toggleFullscreen}
                                    className="flex items-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-indigo-600  to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white rounded-lg shadow-md transition text-sm font-medium"
                                    title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                                >
                                    {isFullscreen ? (
                                        <Minimize2 className="h-4 w-4" />
                                    ) : (
                                        <Maximize2 className="h-4 w-4" />
                                    )}
                                    <span className="hidden sm:inline">
                                        {isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                                    </span>
                                    <span className="sm:hidden">Full</span>
                                </button>
                            </div>
                        </div>

                        {/* Camera Selection Panel */}
                        <div className="bg-white  rounded-xl shadow p-6">
                            <div className="flex flex-col lg:flex-row gap-8">
                                {/* Camera Selection Panel */}
                                <div className="w-full lg:w-80 transition-all duration-300">
                                        <div className="overflow-y-auto h-[700px]">
                                            {loading ? (
                                                <div className="text-center text-gray-500 py-8">
                                                    <div className="animate-spin h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-3"></div>
                                                    <span>Loading cameras...</span>
                                                </div>
                                            ) : (
                                                <div className="space-y-4">
                                                    {/* Simplified Camera Selection */}
                                                    <div className="flex items-center justify-between mb-4">
                                                        <div>
                                                            <h3 className="text-lg font-semibold text-gray-900">Camera Selection</h3>
                                                            <p className="text-xs text-gray-500 mt-1">Click cameras to add/remove from view</p>
                                                        </div>
                                                        {viewMode === 'live' && (
                                                            <button
                                                                onClick={() => setShowAllCameras(!showAllCameras)}
                                                                className="flex items-center space-x-2 px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                                                            >
                                                                {showAllCameras ? (
                                                                    <>
                                                                        <Users className="h-4 w-4" />
                                                                        <span>All Cameras</span>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <Heart className="h-4 w-4" />
                                                                        <span>Selected ({selectedCameras.length})</span>
                                                                    </>
                                                                )}
                                                            </button>
                                                        )}
                                                    </div>

                                                    {viewMode === 'live' && !showAllCameras ? (
                                                        <CameraPreferences
                                                            cameras={availableCameras}
                                                            onCameraSelect={handleCameraToggle}
                                                            selectedCameras={selectedCameras}
                                                        />
                                                    ) : (
                                                        <AdvancedCameraSearch
                                                            cameras={availableCameras}
                                                            searchTerm={searchTerm}
                                                            onSearchChange={setSearchTerm}
                                                            onCamerasFiltered={setFilteredCameras}
                                                            onCameraSelect={handleCameraToggle}
                                                            onCameraDoubleClick={handleDoubleClick}
                                                            selectedCameras={selectedCameras}
                                                            hasPlaybackPermission={hasPermission('cameras', 'playback')}
                                                        />
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                </div>

                                {/* Camera Display Area */}
                                <div className="flex-1 min-h-0">
                                    <div className="h-[600px] w-full bg-gray-50 rounded-lg border border-gray-200">
                                        <div className="h-full w-full p-2">
                                            {viewMode === 'live' || viewMode === 'favorites' ? (
                                                <LiveViewDisplay 
                                                    cameras={getDisplayCameras()} 
                                                    gridLayout={getGridLayout()}
                                                    isFullscreen={isFullscreen}
                                                    showCameraModal={showFullscreenModal}
                                                />
                                            ) : viewMode === 'map' ? (
                                                <GoogleMapsView
                                                    cameras={availableCameras}
                                                    onCameraClick={(camera) => {
                                                        setPreviousViewMode(viewMode);
                                                        setFullscreenCamera(camera);
                                                        setShowFullscreenModal(true);
                                                    }}
                                                />
                                            ) : (
                                                <ScreenBasedDisplay
                                                    activeScreen={getActiveScreen()}
                                                    screenCameras={screenCameras}
                                                    availableCameras={availableCameras}
                                                    onEditScreen={handleEditScreen}
                                                    onCreateFirstScreen={handleCreateFirstScreen}
                                                    hasScreens={getScreens().length > 0}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            ) : (
                // Fullscreen mode layout - simplified for non-tech users
                <AutoRotationManager
                    isActive={isFullscreen && currentView === 'custom' && preferences.screens?.autoRotation?.enabled}
                    onScreenChange={handleScreenChange}
                    onRotationStateChange={(state) => {
                        setRotationState(state);
                        console.log('Rotation state:', state);
                    }}
                    activityDetectionEnabled={true}
                >
                    {(rotationContext) => (
                        <div className="fixed inset-0 z-50 bg-black">
                        {/* Fullscreen Controls - Top */}
                        <div className="absolute top-0 left-0 right-0 z-20">
                            <div className="bg-gradient-to-b from-black/80 to-transparent p-4">
                                <div className="flex items-center justify-between">
                                    {/* Left side - Screen/View info */}
                                    <div className="flex items-center space-x-4">
                                        {currentView === 'custom' ? (
                                            <>
                                                {/* Screen selector for custom mode */}
                                                {getScreens().length > 1 && (
                                                    <div className="bg-black/50 backdrop-blur-sm rounded-lg px-3 py-2 flex items-center space-x-2">
                                                        <Monitor className="h-4 w-4 text-gray-400" />
                                                        <select
                                                            value={preferences.screens?.activeScreenId || ''}
                                                            onChange={(e) => {
                                                                const newScreenId = e.target.value;
                                                                if (newScreenId) {
                                                                    handleScreenChange(newScreenId);
                                                                }
                                                            }}
                                                            className="bg-transparent text-white text-sm outline-none cursor-pointer"
                                                        >
                                                            {getScreens().map(screen => (
                                                                <option key={screen.id} value={screen.id} className="bg-gray-900">
                                                                    {screen.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                )}
                                                
                                                {/* Screen info */}
                                                {getActiveScreen() && (
                                                    <div className="bg-black/50 backdrop-blur-sm rounded-lg px-3 py-2">
                                                        <div className="flex items-center space-x-2">
                                                            <Grid className="h-4 w-4 text-gray-400" />
                                                            <span className="text-white text-sm">
                                                                {getActiveScreen().grid?.columns}×{getActiveScreen().grid?.rows}
                                                            </span>
                                                            <span className="text-gray-400 text-sm">•</span>
                                                            <span className="text-gray-300 text-sm">
                                                                {screenCameras.length} cameras
                                                            </span>
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                {/* View mode info */}
                                                <div className="bg-black/50 backdrop-blur-sm rounded-lg px-3 py-2">
                                                    <div className="flex items-center space-x-2">
                                                        {currentView === 'live' ? (
                                                            <Video className="h-4 w-4 text-green-400" />
                                                        ) : (
                                                            <Star className="h-4 w-4 text-yellow-400" />
                                                        )}
                                                        <span className="text-white text-sm font-medium">
                                                            {currentView === 'live' ? 'Live View' : 'Favourites'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    
                                    {/* Right side - Controls */}
                                    <div className="flex items-center space-x-2">
                                        {/* Pagination Controls for live/favourites */}
                                        {currentView !== 'custom' && (() => {
                                            const filteredCameras = getDashboardFilteredCameras();
                                            const fullscreenGrid = getFullscreenGridDimensions();
                                            const camerasPerPage = fullscreenGrid.rows * fullscreenGrid.cols;
                                            const totalPages = Math.ceil(filteredCameras.length / camerasPerPage);
                                            
                                            if (totalPages > 1) {
                                                return (
                                                    <div className="bg-black/50 backdrop-blur-sm rounded-lg px-2 py-1 flex items-center">
                                                        <button
                                                            onClick={() => setFullscreenPage(Math.max(0, fullscreenPage - 1))}
                                                            disabled={fullscreenPage === 0}
                                                            className="p-1.5 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-white/10 rounded transition-all"
                                                            title="Previous Page"
                                                        >
                                                            <ChevronLeft className="h-4 w-4 text-white" />
                                                        </button>
                                                        <span className="text-sm px-3 text-white font-medium">
                                                            {fullscreenPage + 1} / {totalPages}
                                                        </span>
                                                        <button
                                                            onClick={() => setFullscreenPage(Math.min(totalPages - 1, fullscreenPage + 1))}
                                                            disabled={fullscreenPage >= totalPages - 1}
                                                            className="p-1.5 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-white/10 rounded transition-all"
                                                            title="Next Page"
                                                        >
                                                            <ChevronRight className="h-4 w-4 text-white" />
                                                        </button>
                                                    </div>
                                                );
                                            }
                                        })()}
                                        
                                        {/* Layout Controls for live/favourites */}
                                        {currentView !== 'custom' && (
                                            <div className="bg-black/50 backdrop-blur-sm rounded-lg p-1 flex items-center">
                                                {['2x2', '3x3', '4x4'].map(layout => (
                                                    <button
                                                        key={layout}
                                                        onClick={() => {
                                                            setFullscreenGridLayout(layout);
                                                            setFullscreenPage(0);
                                                        }}
                                                        className={`px-3 py-1.5 text-sm rounded transition-all ${
                                                            fullscreenGridLayout === layout
                                                                ? 'bg-white/20 text-white font-medium'
                                                                : 'text-gray-300 hover:text-white hover:bg-white/10'
                                                        }`}
                                                    >
                                                        {layout}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                        
                                        {/* Screen navigation for custom mode */}
                                        {currentView === 'custom' && getScreens().length > 1 && (
                                            <div className="bg-black/50 backdrop-blur-sm rounded-lg px-2 py-1 flex items-center">
                                                <button
                                                    onClick={() => {
                                                        const screens = getScreens();
                                                        const currentIndex = screens.findIndex(s => s.id === getActiveScreen()?.id);
                                                        const prevIndex = currentIndex > 0 ? currentIndex - 1 : screens.length - 1;
                                                        handleScreenChange(screens[prevIndex].id);
                                                    }}
                                                    className="p-1.5 hover:bg-white/10 rounded transition-all"
                                                    title="Previous Screen"
                                                >
                                                    <ChevronLeft className="h-4 w-4 text-white" />
                                                </button>
                                                <span className="text-sm px-3 text-white">
                                                    Screen {getScreens().findIndex(s => s.id === getActiveScreen()?.id) + 1} / {getScreens().length}
                                                </span>
                                                <button
                                                    onClick={() => {
                                                        const screens = getScreens();
                                                        const currentIndex = screens.findIndex(s => s.id === getActiveScreen()?.id);
                                                        const nextIndex = currentIndex < screens.length - 1 ? currentIndex + 1 : 0;
                                                        handleScreenChange(screens[nextIndex].id);
                                                    }}
                                                    className="p-1.5 hover:bg-white/10 rounded transition-all"
                                                    title="Next Screen"
                                                >
                                                    <ChevronRight className="h-4 w-4 text-white" />
                                                </button>
                                            </div>
                                        )}
                                        
                                        {/* Exit button */}
                                        <button
                                            onClick={() => {
                                                if (document.exitFullscreen) {
                                                    document.exitFullscreen();
                                                }
                                                setIsFullscreen(false);
                                            }}
                                            className="bg-red-600/70 hover:bg-red-600 backdrop-blur-sm text-white p-2 rounded-lg transition-all flex items-center space-x-2"
                                            title="Exit Fullscreen (ESC)"
                                        >
                                            <X className="h-4 w-4" />
                                            <span className="text-sm font-medium">Exit</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        
                        {/* Pure Camera Display Area - Fullscreen */}
                        <div className="w-full h-full bg-black overflow-hidden">
                            <div className="h-full w-full p-0">
                                <div className="h-full w-full">
                                    {(() => {
                                        console.log('Fullscreen render - currentView:', currentView);
                                        console.log('Fullscreen render - currentPage:', currentPage);
                                        console.log('Fullscreen render - dashboardGridLayout:', dashboardGridLayout);
                                        
                                        // Determine which cameras to show based on current view mode
                                        if (currentView === 'custom') {
                                            // Custom mode: Use screen cameras if available
                                            const activeScreen = getActiveScreen();
                                            console.log('Custom mode fullscreen - Active screen:', activeScreen);
                                            console.log('Screen cameras:', screenCameras);
                                            
                                            if (activeScreen && screenCameras.length > 0) {
                                                const gridLayout = {
                                                    cols: activeScreen.grid.columns,
                                                    rows: activeScreen.grid.rows
                                                };
                                                
                                                return (
                                                    <div className="w-full h-full">
                                                        <div 
                                                            className="grid gap-2 w-full h-full p-2"
                                                            style={{
                                                                gridTemplateColumns: `repeat(${gridLayout.cols}, 1fr)`,
                                                                gridTemplateRows: `repeat(${gridLayout.rows}, 1fr)`,
                                                            }}
                                                        >
                                                            {Array.from({ length: gridLayout.rows * gridLayout.cols }).map((_, index) => {
                                                                const row = Math.floor(index / gridLayout.cols) + 1;
                                                                const col = (index % gridLayout.cols) + 1;
                                                                const screenCamera = screenCameras.find(sc => 
                                                                    sc.position?.row === row && sc.position?.col === col
                                                                );
                                                                const camera = screenCamera ? availableCameras.find(c => c._id === (screenCamera._id || screenCamera.cameraId)) : null;
                                                                const cameraId = camera?._id;
                                                                const isLoading = camera && autoLoadCameras.has(cameraId) && !readyStreamCameras.has(cameraId);
                                                                const isReady = camera && readyStreamCameras.has(cameraId);
                                                                    
                                                                    return (
                                                                        <div
                                                                            key={`${row}-${col}`}
                                                                            className="relative bg-gray-800 rounded overflow-hidden"
                                                                        >
                                                                            {camera ? (
                                                                                <>
                                                                                    {/* Show GIF thumbnail first, then stream */}
                                                                                    {!isReady && (
                                                                                        <CachedSnapshot 
                                                                                            cameraId={camera._id}
                                                                                            refreshKey={thumbnailKeys[camera._id] || 0}
                                                                                            alt={`${camera.name} snapshot`}
                                                                                            className="absolute inset-0 w-full h-full object-cover"
                                                                                            showLoadingIndicator={false}
                                                                                            options={{
                                                                                                gifTimeout: 15000,
                                                                                                jpegTimeout: 8000,
                                                                                                gifDuration: 3,
                                                                                                gifFps: 12
                                                                                            }}
                                                                                        />
                                                                                    )}
                                                                                    
                                                                                    {/* Load stream (hidden initially if not ready) */}
                                                                                    {autoLoadCameras.has(cameraId) && (
                                                                                        <div className={`absolute inset-0 ${!isReady ? 'opacity-0' : 'opacity-100'} transition-opacity duration-500`}>
                                                                                            <SimpleCameraStream 
                                                                                                camera={camera}
                                                                                                onStreamReady={handleStreamReady}
                                                                                                onStreamError={handleStreamError}
                                                                                                className="w-full h-full"
                                                                                                hideControls={true}
                                                                                            />
                                                                                        </div>
                                                                                    )}
                                                                                    
                                                                                    {/* Camera name overlay with status dot */}
                                                                                    <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white p-2">
                                                                                        <div className="flex items-center justify-between">
                                                                                            <div className="flex-1">
                                                                                                <div className="text-sm font-medium truncate">{camera.name}</div>
                                                                                                <div className="text-xs text-gray-300">
                                                                                                    {camera.isOnline ? (isReady ? 'Live' : isLoading ? 'Loading...' : 'Online') : 'Offline'}
                                                                                                </div>
                                                                                            </div>
                                                                                            <div className={`w-3 h-3 rounded-full shadow-lg ml-2 ${
                                                                                                isReady ? 'bg-green-500 animate-pulse' : 
                                                                                                isLoading ? 'bg-orange-500 animate-pulse' : 
                                                                                                'bg-gray-500'
                                                                                            }`} />
                                                                                        </div>
                                                                                    </div>
                                                                                </>
                                                                            ) : (
                                                                                <div className="w-full h-full flex items-center justify-center bg-gray-900">
                                                                                    <div className="text-gray-600 text-xs">Empty</div>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })}
                                                        </div>
                                                    </div>
                                                );
                                            } else {
                                                // No screen selected, show empty state
                                                return (
                                                    <div className="h-full flex items-center justify-center text-gray-500">
                                                        <div className="text-center">
                                                            <Layout className="h-16 w-16 mx-auto mb-6 text-gray-400" />
                                                            <p className="text-xl font-medium mb-3 text-white">No Custom Screen Selected</p>
                                                            <p className="text-sm mb-4 text-gray-300">Please select a custom screen to view cameras in fullscreen mode</p>
                                                        </div>
                                                    </div>
                                                );
                                            }
                                        } else {
                                            // Live or Favourites mode: Show current page cameras
                                            console.log('Processing live/favourites mode in fullscreen');
                                            const filteredCameras = getDashboardFilteredCameras();
                                            console.log('Filtered cameras:', filteredCameras.length);
                                            
                                            // Use fullscreen grid dimensions and page
                                            const fullscreenGrid = getFullscreenGridDimensions();
                                            const fullscreenCapacity = fullscreenGrid.rows * fullscreenGrid.cols;
                                            
                                            // Calculate cameras for current fullscreen page
                                            const totalAvailable = filteredCameras.length;
                                            const startIndex = fullscreenPage * fullscreenCapacity;
                                            const endIndex = Math.min(startIndex + fullscreenCapacity, totalAvailable);
                                            const fullscreenCameras = filteredCameras.slice(startIndex, endIndex);
                                            
                                            console.log('Fullscreen page:', fullscreenPage);
                                            console.log('Fullscreen cameras to display:', fullscreenCameras.length);
                                            console.log('Camera IDs:', fullscreenCameras.map(c => c._id));
                                            
                                            return (
                                                <div className="w-full h-full">
                                                    <div 
                                                        className="grid gap-1 w-full h-full p-1"
                                                        style={{
                                                            gridTemplateColumns: `repeat(${fullscreenGrid.cols}, 1fr)`,
                                                            gridTemplateRows: `repeat(${fullscreenGrid.rows}, 1fr)`,
                                                        }}
                                                    >
                                                        {Array.from({ length: fullscreenGrid.rows * fullscreenGrid.cols }).map((_, index) => {
                                                            const camera = fullscreenCameras[index];
                                                            const cameraId = camera?._id;
                                                            const isLoading = camera && autoLoadCameras.has(cameraId) && !readyStreamCameras.has(cameraId);
                                                            const isReady = camera && readyStreamCameras.has(cameraId);
                                                            
                                                            return (
                                                                <div
                                                                    key={`${fullscreenPage}-${index}`}
                                                                    className="group relative bg-gray-800 rounded overflow-hidden"
                                                                >
                                                                    {camera ? (
                                                                        <>
                                                                            {/* Show GIF thumbnail first, then stream */}
                                                                            {!isReady && (
                                                                                <CachedSnapshot 
                                                                                    cameraId={camera._id}
                                                                                    refreshKey={thumbnailKeys[camera._id] || 0}
                                                                                    alt={`${camera.name} snapshot`}
                                                                                    className="absolute inset-0 w-full h-full object-cover"
                                                                                    showLoadingIndicator={false}
                                                                                    options={{
                                                                                        gifTimeout: 15000,
                                                                                        jpegTimeout: 8000,
                                                                                        gifDuration: 3,
                                                                                        gifFps: 12
                                                                                    }}
                                                                                />
                                                                            )}
                                                                            
                                                                            {/* Load stream (hidden initially if not ready) */}
                                                                            {autoLoadCameras.has(cameraId) && (
                                                                                <div className={`absolute inset-0 ${!isReady ? 'opacity-0' : 'opacity-100'} transition-opacity duration-500`}>
                                                                                    <SimpleCameraStream 
                                                                                        camera={camera}
                                                                                        onStreamReady={handleStreamReady}
                                                                                        onStreamError={handleStreamError}
                                                                                        className="w-full h-full"
                                                                                        hideControls={true}
                                                                                    />
                                                                                </div>
                                                                            )}
                                                                            

                                                                            {/* Camera name overlay with status dot */}
                                                                            <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white p-2">
                                                                                <div className="flex items-center justify-between">
                                                                                    <div className="flex-1">
                                                                                        <div className="text-sm font-medium truncate">{camera.name}</div>
                                                                                        <div className="text-xs text-gray-300">
                                                                                            {camera.isOnline ? (isReady ? 'Live' : isLoading ? 'Loading...' : 'Online') : 'Offline'}
                                                                                        </div>
                                                                                    </div>
                                                                                    {/* Status indicator dot at bottom */}
                                                                                    <div className={`w-3 h-3 rounded-full shadow-lg ml-2 ${
                                                                                        isReady ? 'bg-green-500 animate-pulse' : 
                                                                                        isLoading ? 'bg-orange-500 animate-pulse' : 
                                                                                        'bg-gray-500'
                                                                                    }`} />
                                                                                </div>
                                                                            </div>
                                                                        </>
                                                                    ) : (
                                                                        <div className="w-full h-full flex items-center justify-center bg-gray-900">
                                                                            <div className="text-gray-600 text-xs">Empty</div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            );
                                        }
                                    })()}
                                </div>
                            </div>
                            
                            {/* Floating Controls for Fullscreen - Completely removed for now */}
                            {false && currentView === 'custom' && (
                                <FloatingControls
                                    isVisible={isFullscreen}
                                    isFullscreen={isFullscreen}
                                    onExitFullscreen={() => {
                                        if (document.exitFullscreen) {
                                            document.exitFullscreen();
                                        } else if (document.webkitExitFullscreen) {
                                            document.webkitExitFullscreen();
                                        } else if (document.mozCancelFullScreen) {
                                            document.mozCancelFullScreen();
                                        } else if (document.msExitFullscreen) {
                                            document.msExitFullscreen();
                                        }
                                    }}
                                    onScreenChange={handleScreenChange}
                                    onEditScreen={handleEditScreen}
                                    onOpenScreenManager={() => setShowScreenManager(true)}
                                    availableCameras={availableCameras}
                                    currentScreenCameras={getCurrentCameras()}
                                    autoHideDelay={getActiveScreen()?.displaySettings?.autoHideControls || 3000}
                                    rotationState={rotationState}
                                />
                            )}
                        </div>
                        </div>
                    )}
                </AutoRotationManager>
            )}

            {/* Picture in Picture */}
            <PictureInPicture
                camera={pipCamera}
                isVisible={showPip}
                onClose={() => {
                    setShowPip(false);
                    setPipCamera(null);
                }}
            />

            {/* Screen Management Modals */}
            <ScreenManager
                isOpen={showScreenManager}
                onClose={() => setShowScreenManager(false)}
                onEditScreen={handleEditScreen}
                onViewScreen={handleViewScreen}
                availableCameras={availableCameras}
            />

            <DragDropScreenBuilder
                screenId={editingScreenId}
                isOpen={showScreenBuilder}
                onClose={() => {
                    setShowScreenBuilder(false);
                    setEditingScreenId(null);
                }}
                availableCameras={availableCameras}
                onSave={(screenConfig) => {
                    if (editingScreenId && editingScreenId !== 'new') {
                        // Update existing screen
                        setShowScreenBuilder(false);
                        setEditingScreenId(null);
                        toast.success('View updated successfully');
                    } else {
                        // Create new screen
                        const screenId = createScreen(screenConfig);
                        setShowScreenBuilder(false);
                        setEditingScreenId(null);
                        handleScreenChange(screenId);
                        toast.success('View created successfully');
                    }
                }}
            />

            {/* Fullscreen Camera Modal */}
            {showFullscreenModal && fullscreenCamera && (
                <div className="fixed inset-0 bg-black z-50 flex items-center justify-center">
                    {/* Professional Media Player Container */}
                    <div className="w-full h-full max-w-[95vw] max-h-[95vh] bg-black rounded-lg overflow-hidden shadow-2xl relative">
                        {/* Camera Stream - Full Container */}
                        <div className="w-full h-full relative bg-black">
                            <SimpleCameraStream 
                                camera={fullscreenCamera} 
                                onStreamReady={handleStreamReady}
                                onStreamError={handleStreamError}
                                hideControls={true}
                            />
                        </div>

                        {/* Professional Control Bar */}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/70 to-transparent p-6">
                            <div className="flex items-center justify-between">
                                {/* Left side - Camera info */}
                                <div className="flex items-center space-x-4">
                                    <div className="flex items-center space-x-2">
                                        <div className={`w-2 h-2 rounded-full ${fullscreenCamera.isOnline ? 'bg-green-500' : 'bg-red-500'}`} />
                                        <span className="text-white text-sm font-medium">
                                            {fullscreenCamera.isOnline ? 'LIVE' : 'OFFLINE'}
                                        </span>
                                    </div>
                                    <div className="text-white/80 text-sm">
                                        {fullscreenCamera.name}
                                    </div>
                                </div>

                                {/* Right side - Controls */}
                                <div className="flex items-center space-x-3">
                                    {/* Refresh button */}
                                    <button
                                        onClick={() => {
                                            refreshThumbnail(fullscreenCamera._id);
                                            toast.success(`Refreshing ${fullscreenCamera.name}`, { duration: 1500 });
                                        }}
                                        className="flex items-center justify-center w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full transition-all duration-200 text-white backdrop-blur-sm"
                                        title="Refresh Camera"
                                    >
                                        <RefreshCw className="h-5 w-5" />
                                    </button>

                                    {/* Audio toggle button */}
                                    <button
                                        onClick={() => toggleAudio(fullscreenCamera._id)}
                                        className="flex items-center justify-center w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full transition-all duration-200 text-white backdrop-blur-sm"
                                        title={audioStates[fullscreenCamera._id] ? 'Disable Audio' : 'Enable Audio'}
                                    >
                                        {(audioStates[fullscreenCamera._id] || false) ? 
                                            <Volume2 className="h-5 w-5" /> : 
                                            <VolumeX className="h-5 w-5" />
                                        }
                                    </button>

                                    {/* Fullscreen toggle */}
                                    <button
                                        onClick={() => {
                                            if (!document.fullscreenElement) {
                                                document.documentElement.requestFullscreen().catch(err => {
                                                    console.log('Error attempting to enable fullscreen:', err);
                                                });
                                            } else {
                                                document.exitFullscreen();
                                            }
                                        }}
                                        className="flex items-center justify-center w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full transition-all duration-200 text-white backdrop-blur-sm"
                                        title="Toggle Fullscreen"
                                    >
                                        <Maximize2 className="h-5 w-5" />
                                    </button>
                                    
                                    {/* Close button */}
                                    <button
                                        onClick={handleCloseFullscreen}
                                        className="flex items-center justify-center w-10 h-10 bg-red-600/80 hover:bg-red-600 rounded-full transition-all duration-200 text-white backdrop-blur-sm"
                                        title="Close"
                                    >
                                        <X className="h-5 w-5" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CameraMonitoring;